import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import settingsService from '../../services/settingsService';
import './Login.css';

const Login = ({ onLoginSuccess, loginError }) => {
    const [mode, setMode] = useState('login'); // 'login' | 'register' | 'pending' | 'forgot' | 'resetSent'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [phone, setPhone] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [pendingMessage, setPendingMessage] = useState('');
    const [branding, setBranding] = useState({});

    useEffect(() => {
        let cancelled = false;
        settingsService.getPublicSettings().then(settings => {
            if (!cancelled) setBranding(settings);
        });
        return () => { cancelled = true; };
    }, []);

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const data = await authService.login(email, password);
            if (onLoginSuccess) {
                onLoginSuccess(data.user);
            }
        } catch (err) {
            setError(err.message || 'Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const subdomain = settingsService.getSubdomain();
            const data = await authService.register({
                email, password, firstName, lastName, companyName, phone,
                ...(subdomain && { subdomain })
            });

            if (data.approved === false) {
                // Manual approval required
                setPendingMessage(data.message || 'Your registration has been submitted. You should receive approval within 24-48 hours.');
                setMode('pending');
            } else {
                // Auto-approved — user is logged in
                if (onLoginSuccess && data.user) {
                    onLoginSuccess(data.user);
                }
            }
        } catch (err) {
            setError(err.message || 'Registration failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const switchToRegister = (e) => {
        e.preventDefault();
        setError('');
        setMode('register');
    };

    const switchToLogin = (e) => {
        e.preventDefault();
        setError('');
        setMode('login');
    };

    const switchToForgot = (e) => {
        e.preventDefault();
        setError('');
        setMode('forgot');
    };

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await authService.requestPasswordReset(email);
            setMode('resetSent');
        } catch (err) {
            setError(err.message || 'Failed to send reset link. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const loginLogoUrl = branding.login_logo_url;
    const loginBgUrl = branding.login_background_url;
    const companyDisplayName = branding.portal_name || branding.company_name || 'ConfigSelect';

    const leftStyle = loginBgUrl
        ? { backgroundImage: `url(${loginBgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : {};

    return (
        <div className="login-container">
            <div className={`login-left ${loginBgUrl ? 'login-left-image' : ''}`} style={leftStyle}>
                <div className={`login-logo-container ${loginBgUrl ? 'login-logo-overlay' : ''}`}>
                    {loginLogoUrl ? (
                        <img src={loginLogoUrl} alt={companyDisplayName} className="login-logo-img" />
                    ) : (
                        <>
                            <h1 className="login-brand">{companyDisplayName}</h1>
                            <p className="login-tagline">Product Configurator</p>
                        </>
                    )}
                </div>
            </div>
            <div className="login-right">
                <div className="login-form-container">

                    {mode === 'login' && (
                        <React.Fragment>
                            <h2>Welcome Back</h2>
                            <p className="login-subtitle">Sign in to continue</p>

                            {loginError && <div className="login-error">{loginError}</div>}
                            {error && <div className="login-error">{error}</div>}

                            <form onSubmit={handleLoginSubmit}>
                                <div className="form-group">
                                    <label htmlFor="email">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Enter your email"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="password">Password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter your password"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="login-button"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Signing in...' : 'LOGIN'}
                                </button>
                            </form>
                            <div className="login-footer">
                                <a href="#" className="forgot-password" onClick={switchToForgot}>Lost your password?</a>
                                <div className="register-link">
                                    Not Registered Yet?{' '}
                                    <a href="#" onClick={switchToRegister}>Click here to register</a>
                                </div>
                            </div>
                        </React.Fragment>
                    )}

                    {mode === 'register' && (
                        <React.Fragment>
                            <h2>Create Account</h2>
                            <p className="login-subtitle">Register for access</p>

                            {error && <div className="login-error">{error}</div>}

                            <form onSubmit={handleRegisterSubmit}>
                                <div className="form-row">
                                    <div className="form-group half">
                                        <label htmlFor="firstName">First Name</label>
                                        <input
                                            type="text"
                                            id="firstName"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            placeholder="First name"
                                            required
                                        />
                                    </div>
                                    <div className="form-group half">
                                        <label htmlFor="lastName">Last Name</label>
                                        <input
                                            type="text"
                                            id="lastName"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            placeholder="Last name"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="regEmail">Email</label>
                                    <input
                                        type="email"
                                        id="regEmail"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Enter your email"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="regPassword">Password</label>
                                    <input
                                        type="password"
                                        id="regPassword"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Create a password"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="companyName">Company Name</label>
                                    <input
                                        type="text"
                                        id="companyName"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        placeholder="Your company"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="phone">Phone</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="Phone number"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="login-button"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Registering...' : 'REGISTER'}
                                </button>
                            </form>
                            <div className="login-footer">
                                <a href="#" onClick={switchToLogin} className="forgot-password">
                                    Already have an account? Sign in
                                </a>
                            </div>
                        </React.Fragment>
                    )}

                    {mode === 'pending' && (
                        <div className="register-pending-message">
                            <div className="pending-icon">&#9989;</div>
                            <h2>Registration Submitted</h2>
                            <p>{pendingMessage}</p>
                            <a href="#" onClick={switchToLogin} className="login-button" style={{ display: 'inline-block', textAlign: 'center', textDecoration: 'none', marginTop: '20px' }}>
                                Back to Login
                            </a>
                        </div>
                    )}

                    {mode === 'forgot' && (
                        <React.Fragment>
                            <h2>Reset Password</h2>
                            <p className="login-subtitle">Enter your email to receive a reset link</p>

                            {error && <div className="login-error">{error}</div>}

                            <form onSubmit={handleForgotSubmit}>
                                <div className="form-group">
                                    <label htmlFor="forgotEmail">Email</label>
                                    <input
                                        type="email"
                                        id="forgotEmail"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Enter your email"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="login-button"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Sending...' : 'Send Reset Link'}
                                </button>
                            </form>
                            <div className="login-footer">
                                <a href="#" onClick={switchToLogin} className="forgot-password">
                                    Back to Sign In
                                </a>
                            </div>
                        </React.Fragment>
                    )}

                    {mode === 'resetSent' && (
                        <div className="register-pending-message">
                            <div className="pending-icon">&#9993;</div>
                            <h2>Check Your Email</h2>
                            <p>If an account exists with that email, we've sent a password reset link. Please check your inbox and spam folder.</p>
                            <a href="#" onClick={switchToLogin} className="login-button" style={{ display: 'inline-block', textAlign: 'center', textDecoration: 'none', marginTop: '20px' }}>
                                Back to Login
                            </a>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default Login;
